import { NameUtil, RestrictTo, useConfig, useDate } from '@bechtel/client-common';
import {
  Avatar,
  AvatarComponentProps,
  AvatarGroup,
  AvatarGroupSpacing,
  AvatarSize,
  Box,
  Icon,
  IconSize,
  Spinner,
  Text,
} from '@bechtel/gds';
import {
  CrewChangeRequest,
  CrewChangeRequestMoveType,
  CrewChangeRequestStatus,
  CrewChangeRequestStatusCode,
  CrewMember,
} from '@bechtel/types';
import { Trans, t } from '@lingui/macro';
import { parseISO } from 'date-fns';
import { concat, filter, map, maxBy, minBy } from 'lodash';
import React from 'react';
import { useCrewChangePermissions } from '../../hooks/use-crew-change-permission';
import { CrewChangeActions } from '../../types';
import {
  ActionButtonStyled,
  CancelActionButtonStyled,
  ClosedActionButtonStyled,
  CrewChangeItemCardStyled,
  CrewChangeItemContainerStyled,
} from './styled';

interface CrewChangeRequestProps {
  crewChangeRequest: CrewChangeRequest;
  getFullName: NameUtil['getFullName'];
  onAction: (crewChangeRequestId: string, action: CrewChangeActions) => void;
  actionedCrewChangeRequestId: string;
  approveProgress?: boolean;
  declineProgress?: boolean;
  cancelProgress?: boolean;
}

export const CrewChangeItem: React.FC<CrewChangeRequestProps> = ({
  crewChangeRequest,
  getFullName,
  onAction,
  actionedCrewChangeRequestId,
  approveProgress,
  declineProgress,
  cancelProgress,
}) => {
  const { formatRelativeInWords } = useDate();
  const { dateFormat, timeFormat } = useConfig();
  const { id, moveType, affectedCrewMembers, originCrew, statusHistory, destinationCrew, currentStatus } =
    crewChangeRequest;
  const { canApproveDeclineRequest, canCancelRequest, canApproveAllRequest } = useCrewChangePermissions();
  const actionInfo: CrewChangeRequestStatus = maxBy(statusHistory, 'createdAt') as CrewChangeRequestStatus;
  const createdInfo: CrewChangeRequestStatus = minBy(statusHistory, 'createdAt') as CrewChangeRequestStatus;
  const sourceCrewMember = moveType == CrewChangeRequestMoveType.CREW ? originCrew.generalForeman : originCrew.foreman;
  const destinationCrewMember =
    moveType == CrewChangeRequestMoveType.CREW ? destinationCrew.generalForeman : destinationCrew.foreman;

  const canApproveDecline =
    canApproveDeclineRequest(
      concat(originCrew.foreman, originCrew.generalForeman, originCrew.superintendent),
      concat(destinationCrew.foreman, destinationCrew.generalForeman, destinationCrew.superintendent),
      createdInfo.createdBy
    ) || canApproveAllRequest;

  const canCancel = canCancelRequest(createdInfo.createdBy);
  //Filter out the foreman in affected crew Members
  const affectedCraftCrewMembers: CrewMember[] =
    moveType == CrewChangeRequestMoveType.CRAFT
      ? affectedCrewMembers
      : filter(affectedCrewMembers, (crewMember) => crewMember.id != originCrew.foreman?.id);
  //Form All Crew Members AvatarComponent Props
  const allAffectedCrewMemberAvatars: AvatarComponentProps[] = map(
    affectedCraftCrewMembers,
    (affectedCraftCrewMember) => {
      return {
        src: affectedCraftCrewMember.imageUrl ? affectedCraftCrewMember.imageUrl : '',
        alt: getFullName(affectedCraftCrewMember.firstName, affectedCraftCrewMember.lastName),
      };
    }
  );
  //Form Foreman AvatarComponent Props
  const affectedForemanAvatar: AvatarComponentProps | null =
    moveType == CrewChangeRequestMoveType.CREW
      ? {
          src: originCrew.foreman?.imageUrl ? originCrew.foreman.imageUrl : '',
          alt: getFullName(originCrew.foreman?.firstName, originCrew.foreman?.lastName),
        }
      : null;

  return (
    <CrewChangeItemCardStyled status={currentStatus}>
      <CrewChangeItemContainerStyled>
        <Box style={{ gridRow: 1 }} alignItems={{ sm: 'center' }}>
          <Text size="400" weight="600" family="graphik" color="grey.1000">
            {moveType == CrewChangeRequestMoveType.CREW
              ? t`Move Entire ${originCrew.crewNumber} Crew (${affectedCrewMembers.length})`
              : concat(t`Move Craft`, ` (${affectedCrewMembers.length})`)}
          </Text>
        </Box>
        <Box style={{ gridRow: 2 }} flexDirection={{ sm: 'row' }}>
          <Text size="250" family="graphikCompact" color="grey.600">
            {getFullName(actionInfo?.createdBy.firstName, actionInfo?.createdBy.lastName)}&nbsp;
            {actionInfo.status == CrewChangeRequestStatusCode.PENDING_APPROVAL
              ? t`requested`
              : actionInfo.status == CrewChangeRequestStatusCode.APPROVED
                ? t`approved`
                : actionInfo.status == CrewChangeRequestStatusCode.DECLINED
                  ? t`declined`
                  : t`cancelled`}
          </Text>
          <Text size="250" family="graphikCompact" color="grey.600" weight="600">
            &nbsp;
            {formatRelativeInWords(
              parseISO(actionInfo?.createdAt),
              new Date(),
              RestrictTo.Day,
              dateFormat,
              ` @ ${timeFormat}`
            )}
          </Text>
        </Box>
        <Box style={{ gridRow: 4 }} justifyContent={{ sm: 'space-between' }}>
          <Box alignItems={{ sm: 'center' }}>
            <Box>
              <Avatar
                src={sourceCrewMember.imageUrl ? sourceCrewMember.imageUrl : ''}
                size={AvatarSize.MEDIUM}
                data-testid="from-avatar"
                css={`
                  background-color: ${({ theme }) => theme.colors.grey[200]};
                  color: ${({ theme }) => theme.colors.grey[600]};
                `}
              ></Avatar>
            </Box>
            <Box flexDirection={{ sm: 'column' }} ml={{ sm: '200' }}>
              <Text size="200" color="grey.600">
                <Trans> From</Trans>:
              </Text>
              <Text
                size="400"
                family="graphikCompact"
                color="grey.1000"
                style={{ marginTop: '4px' }}
                data-testid="from-crewmember-name"
              >
                {getFullName(sourceCrewMember.firstName, sourceCrewMember.lastName)}
              </Text>
            </Box>
          </Box>
          <Box alignItems={{ sm: 'center' }}>
            {affectedForemanAvatar && (
              <>
                <AvatarGroup
                  maxCount={1}
                  data={[affectedForemanAvatar]}
                  size={AvatarSize.MEDIUM}
                  spacing={AvatarGroupSpacing.SMALL}
                  css={`
                    background-color: ${({ theme }) => theme.colors.grey[200]};
                    color: ${({ theme }) => theme.colors.grey[600]};
                  `}
                  data-testid="craft-affected-avatar-group"
                />
                <Box mx={{ sm: '100' }} data-testid="foreman-affected-avatar-group">
                  <Icon
                    icon="plus"
                    size={IconSize.XSMALL}
                    css={`
                      color: ${({ theme }) => theme.colors.grey[500]};
                    `}
                  ></Icon>
                </Box>
              </>
            )}
            <AvatarGroup
              maxCount={affectedForemanAvatar ? 5 : 6}
              data={allAffectedCrewMemberAvatars}
              size={AvatarSize.MEDIUM}
              spacing={AvatarGroupSpacing.SMALL}
              css={`
                background-color: ${({ theme }) => theme.colors.grey[200]};
                color: ${({ theme }) => theme.colors.grey[600]};
              `}
              data-testid="craft-affected-avatar-group"
            />
          </Box>
        </Box>
        <Box style={{ gridRow: 6 }} alignItems={{ sm: 'center' }}>
          <Box>
            <Avatar
              src={destinationCrewMember.imageUrl ? destinationCrewMember.imageUrl : ''}
              size={AvatarSize.MEDIUM}
              data-testid="to-avatar"
              css={`
                background-color: ${({ theme }) => theme.colors.grey[200]};
                color: ${({ theme }) => theme.colors.grey[600]};
              `}
            ></Avatar>
          </Box>
          <Box flexDirection={{ sm: 'column' }} ml={{ sm: '200' }}>
            <Text size="200" color="grey.600">
              <Trans>To</Trans>:
            </Text>
            <Text size="400" color="grey.1000" style={{ marginTop: '4px' }} data-testid="to-crewmember-name">
              {getFullName(destinationCrewMember.firstName, destinationCrewMember.lastName)}
            </Text>
          </Box>
        </Box>
        <Box style={{ gridRow: 8 }} justifyContent={{ sm: 'space-between' }}>
          {currentStatus == CrewChangeRequestStatusCode.PENDING_APPROVAL ? (
            <>
              {canApproveDecline && (
                <>
                  <Box flexGrow={{ sm: '1' }} mr={{ sm: '400' }} data-testid="approve-decline-action-container">
                    <ActionButtonStyled
                      disabled={actionedCrewChangeRequestId != ''}
                      onClick={(e) => {
                        e.preventDefault();
                        onAction(id, CrewChangeActions.DECLINE);
                      }}
                    >
                      <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                        {!declineProgress && <Icon icon="times" size={IconSize.MEDIUM} fillColor="blue.800" />}
                        {declineProgress && actionedCrewChangeRequestId != '' && id == actionedCrewChangeRequestId && (
                          <Spinner />
                        )}
                      </Box>
                      <Text size="300" color="grey.1000">
                        <Trans>Decline</Trans>
                      </Text>
                    </ActionButtonStyled>
                  </Box>
                  <Box flexGrow={{ sm: '1' }}>
                    <ActionButtonStyled
                      disabled={actionedCrewChangeRequestId != ''}
                      onClick={(e) => {
                        e.preventDefault();
                        onAction(id, CrewChangeActions.APPROVE);
                      }}
                    >
                      <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                        {!approveProgress && <Icon icon="check" size={IconSize.MEDIUM} fillColor="blue.800" />}
                        {approveProgress && actionedCrewChangeRequestId != '' && id == actionedCrewChangeRequestId && (
                          <Spinner />
                        )}
                      </Box>
                      <Text size="300" color="grey.1000">
                        <Trans>Approve</Trans>
                      </Text>
                    </ActionButtonStyled>
                  </Box>
                </>
              )}
              {canCancel && (
                <Box flexGrow={{ sm: '1' }}>
                  <CancelActionButtonStyled
                    disabled={actionedCrewChangeRequestId != ''}
                    onClick={(e) => {
                      e.preventDefault();
                      onAction(id, CrewChangeActions.CANCEL);
                    }}
                  >
                    <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                      {!cancelProgress && <Icon icon="times" size={IconSize.MEDIUM} fillColor="grey.1000" />}
                      {cancelProgress && actionedCrewChangeRequestId != '' && id == actionedCrewChangeRequestId && (
                        <Spinner />
                      )}
                    </Box>
                    <Text size="300" color="grey.1000" data-testid="cancelled-text">
                      <Trans>Cancel Request</Trans>
                    </Text>
                  </CancelActionButtonStyled>
                </Box>
              )}
            </>
          ) : (
            <Box flexGrow={{ sm: '1' }}>
              <ClosedActionButtonStyled>
                <Text size="300" color="grey.1000" data-testid="closed-placebo-text">
                  <Trans>Closed</Trans>
                </Text>
              </ClosedActionButtonStyled>
            </Box>
          )}
        </Box>
      </CrewChangeItemContainerStyled>
    </CrewChangeItemCardStyled>
  );
};
